import { useCallback, useEffect, useRef, useState } from "react";

import { useAtomValue } from "jotai";

import { accessKeyAtom } from "@/atoms/auth.atoms";

import authService from "@/services/auth.service";

const SESSION_CHECK_INTERVAL_MS = 60_000; // 1 minute

/**
 * Periodically validates the current session token against the backend.
 * Returns `true` when the session has expired (401 from /auth/me).
 * Only runs when the user is authenticated (accessKey is present).
 */

export function useSessionValidator() {
  const accessKey = useAtomValue(accessKeyAtom);

  const [sessionExpired, setSessionExpired] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkSession = useCallback(async () => {
    if (!accessKey) return;

    const valid = await authService.validateToken();

    if (!valid) setSessionExpired(true);
  }, [accessKey]);

  // Reset expired state when user logs out

  useEffect(() => {
    if (!accessKey) setSessionExpired(false);
  }, [accessKey]);

  // Periodic polling

  useEffect(() => {
    if (!accessKey) return;

    intervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [accessKey, checkSession]);

  // Also listen for the auth:session-expired event from api.ts

  useEffect(() => {
    const handler = () => setSessionExpired(true);

    window.addEventListener("auth:session-expired", handler);

    return () => window.removeEventListener("auth:session-expired", handler);
  }, []);

  return sessionExpired;
}
